import React from 'react';
import { Tooltip } from '@material-ui/core';
import styles from './StudentCourseTotalTable.module.css';
import MLButton from '../../../components/MLButton';
import MLIcon from '../../../components/MLIcon';
import StudentCourseTotalRow from './StudentCourseTotalRow';
import {
  LOW_SCORE_THRESHOLD,
  LOW_SCORE_THRESHOLD_PERCENT,
} from '../../constants';
import {
  useSortableTable,
  ORDER_OPTIONS,
} from '../../../hooks/useSortableTable';

const COLUMN_HEADERS = {
  STUDENT_NAME: 'Student Name',
  CURRENT_TOTAL: 'Current Week',
  PREVIOUS_TOTAL: 'Previous Week',
  CHANGE: 'Change',
};

const getGradeChange = (student) => {
  if (student.currentGrade == null || student.previousGrade == null)
    return null;
  return student.currentGrade - student.previousGrade;
};

const getStudentKey = (student) =>
  student.user_id || student.email || JSON.stringify(student);

const sortStudents = (students, sortBy, sortOrder) => {
  return [...students].sort((a, b) => {
    let comparison = 0;

    if (sortBy === COLUMN_HEADERS.STUDENT_NAME) {
      const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
      const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
      comparison = nameA.localeCompare(nameB);
    } else if (sortBy === COLUMN_HEADERS.CURRENT_TOTAL) {
      comparison = (a.currentGrade ?? 0) - (b.currentGrade ?? 0);
    } else if (sortBy === COLUMN_HEADERS.PREVIOUS_TOTAL) {
      comparison = (a.previousGrade ?? 0) - (b.previousGrade ?? 0);
    } else if (sortBy === COLUMN_HEADERS.CHANGE) {
      comparison = (getGradeChange(a) ?? 0) - (getGradeChange(b) ?? 0);
    }

    return sortOrder === ORDER_OPTIONS.ASC ? comparison : -comparison;
  });
};

export const StudentCourseTotalTable = ({
  students,
  currentWeekLabel,
  previousWeekLabel,
  handleISGLaunch,
  setOpen,
}) => {
  const { sortBy, sortOrder, iconType, handleColumnClick } = useSortableTable(
    COLUMN_HEADERS.CURRENT_TOTAL
  );

  const sortedStudents = sortStudents(students, sortBy, sortOrder);

  const onCopyEmailsClick = () => {
    const emails = students
      .filter(
        (student) =>
          student.currentGrade != null &&
          student.currentGrade <= LOW_SCORE_THRESHOLD &&
          student.email
      )
      .map((student) => student.email)
      .join(',');

    navigator.clipboard.writeText(emails);
  };

  const renderCopyEmailsButton = () => {
    const tooltipText = `Copy emails of students with a course total of ${LOW_SCORE_THRESHOLD_PERCENT}% or below in the current week (respecting your search) to your clipboard.`;
    return (
      <Tooltip title={tooltipText} placement="top" arrow enterDelay={400}>
        <div>
          <MLButton
            className={styles.copyEmailsBtn}
            btnType="secondary"
            onClick={onCopyEmailsClick}
            testId="course_total_alert_copy_emails_btn"
          >
            <MLIcon type="item_copy" className={styles.copyIcon} />
            <span>Copy Alert Student Emails</span>
          </MLButton>
        </div>
      </Tooltip>
    );
  };

  const renderHeader = (sortKey, displayLabel) => (
    <button
      className={styles.headerText}
      onClick={() => handleColumnClick(sortKey)}
    >
      {displayLabel || sortKey}
      {sortBy === sortKey && (
        <MLIcon className={styles.caretIcon} type={iconType} />
      )}
    </button>
  );

  return (
    <table className={styles.studentTable}>
      <thead>
        <tr className={styles.tableHeader}>
          <th className={styles.studentNameHeader}>
            {renderHeader(COLUMN_HEADERS.STUDENT_NAME)}
            {renderCopyEmailsButton()}
          </th>
          <th className={styles.courseTotalHeader}>
            {renderHeader(
              COLUMN_HEADERS.CURRENT_TOTAL,
              currentWeekLabel || COLUMN_HEADERS.CURRENT_TOTAL
            )}
          </th>
          <th className={styles.courseTotalHeader}>
            {renderHeader(
              COLUMN_HEADERS.PREVIOUS_TOTAL,
              previousWeekLabel || COLUMN_HEADERS.PREVIOUS_TOTAL
            )}
          </th>
          <th className={styles.courseTotalHeader}>
            {renderHeader(COLUMN_HEADERS.CHANGE)}
          </th>
        </tr>
      </thead>
      <tbody>
        {sortedStudents.map((student) => (
          <StudentCourseTotalRow
            key={getStudentKey(student)}
            student={student}
            handleISGLaunch={handleISGLaunch}
            setOpen={setOpen}
          />
        ))}
      </tbody>
    </table>
  );
};
